/**
 * Beauty Salons - Dashboard Page
 * Overview metrics for beauty salons
 */

import { useEffect, useMemo } from 'react';
import { useIndustryServices } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Scissors, Users, DollarSign, TrendingUp, Calendar, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MainLayout } from '@/components/MainLayout';

export default function BeautySalonsDashboardPage() {
  useEffect(() => {
    document.title = 'Dashboard do Salão | Controle Financeiro';
  }, []);

  const { services, isLoading } = useIndustryServices({ domain: CompanyType.BEAUTY_SALONS });

  const stats = useMemo(() => {
    if (!services) return null;

    const activeServices = services.filter((s) => s.is_active !== false).length;
    const inactiveServices = services.length - activeServices;

    // Aggregate by category
    const categoryMap = new Map<string, number>();
    services.forEach((s) => {
      const category = (s.metadata?.category as string) || 'Outros';
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
    });

    const totalDuration = services.reduce((sum, s) => {
      const duration = (s.metadata?.duration as number) || 0;
      return sum + duration;
    }, 0);

    const totalRevenue = services.reduce((sum, s) => {
      const price = s.price || 0;
      // Simulate appointments for demo purposes
      const appointments = Math.floor(Math.random() * 20) + 10;
      return sum + price * appointments;
    }, 0);

    const avgServicePrice = services.length > 0 ? services.reduce((sum, s) => sum + (s.price || 0), 0) / services.length : 0;

    return {
      totalServices: services.length,
      activeServices,
      inactiveServices,
      categories: categoryMap.size,
      avgDuration: services.length > 0 ? totalDuration / services.length : 0,
      totalRevenue,
      avgServicePrice,
      topCategories: Array.from(categoryMap.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3),
    };
  }, [services]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Carregando...</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Scissors className="h-8 w-8" />
            Dashboard do Salão
          </h1>
          <p className="text-muted-foreground">Visão geral dos seus serviços de beleza e métricas</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Total de Serviços
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalServices || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.activeServices || 0} ativos · {stats?.inactiveServices || 0} inativos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Scissors className="h-4 w-4" />
              Categorias
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.categories || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.topCategories && stats.topCategories.length > 0
                ? `${stats.topCategories[0][0]} (${stats.topCategories[0][1]})`
                : 'Nenhuma categoria'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Duração Média
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{Math.round(stats?.avgDuration || 0)} min</div>
            <p className="text-xs text-muted-foreground mt-1">
              Tempo médio de atendimento
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Valor Médio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats?.avgServicePrice || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Preço médio por serviço</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Projection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Projeção de Receita
          </CardTitle>
          <CardDescription>Baseada em agendamentos simulados</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-green-600">{formatCurrency(stats?.totalRevenue || 0)}</div>
          <p className="text-sm text-muted-foreground mt-2">
            Receita projetada com base nos serviços ativos
          </p>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Serviços</CardTitle>
            <CardDescription>Gerencie seu catálogo de serviços de beleza</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/beauty-salons/services">
              <Button className="w-full">
                <Activity className="mr-2 h-4 w-4" />
                Gerenciar Serviços
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Agendamentos</CardTitle>
            <CardDescription>Calendário de atendimentos</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/beauty-salons/appointments">
              <Button className="w-full" variant="outline">
                <Calendar className="mr-2 h-4 w-4" />
                Ver Agenda
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Receitas</CardTitle>
            <CardDescription>Análise financeira por serviço</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/beauty-salons/revenue">
              <Button className="w-full" variant="outline">
                <DollarSign className="mr-2 h-4 w-4" />
                Ver Relatório
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Top Categories */}
      {stats && stats.topCategories.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Principais Serviços</CardTitle>
            <CardDescription>Serviços mais oferecidos</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.topCategories.map(([category, count], index) => (
                <div key={category} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">#{index + 1}</div>
                    <div>{category}</div>
                  </div>
                  <div className="text-sm text-muted-foreground">{count} serviços</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
    </MainLayout>
  );
}
